import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * shoppable-image — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Shoppable image" section
 * (shrine-pro-ref/sections/shoppable-image.liquid + the `hotspot*` block of assets/base.css). ONE image
 * carries several PRODUCT HOTSPOTS: a `+` (or minimalistic) button pinned at an x/y offset opens a small
 * product modal (image + title + price + optional description + CTA). Content (heading + rich text + button)
 * sits top/right/bottom/left of the image; the whole thing has a SECTION scheme and a distinct CONTENT
 * scheme (matching schemes → the content blends "same-bg", differing → a distinct rounded card).
 *
 * Field-type mapping (Shopify → ours): inline_richtext→text (with bold / italic highlight markup),
 * richtext→richtext, image_picker→image, product→product-picker, url→link, color→color,
 * color_background→gradient, header→group, `visible_if`→`visibleWhen`. Ids are clean camelCase; the
 * labels/options/defaults stay faithful to Shrine. Sizes port at Shrine's 1rem=10px (see Component.tsx).
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const CONTENT = 'Content';
const LAYOUT = 'Content layout';
const IMAGE = 'Image';
const HOTSPOTS = 'Hotspot buttons & modals';
const PRODUCT = 'Product information';
const PADDING = 'Section padding';
/** The 6 Shrine color-scheme options (section + content). */
const SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
    { value: 'custom', label: 'Custom' },
];
/** The 5 hotspot-button / modal color-scheme options (no "custom"). */
const SCHEME_OPTIONS_5 = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
];
const BODY_TEXT_DEFAULT = {
    format: 'html',
    html: '<p>Use this section to hightlight multiple products with one image that contains all of them</p>',
};
export const shoppableImageSchema = {
    type: 'shoppable-image',
    name: 'Shoppable image',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    staticIcons: ['add'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── Content: heading + highlight + size + text + button ──────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: CONTENT,
            default: 'Shoppable image',
            defaultKey: 'section.shoppable_image',
            info: 'Wrap words in ** ** for highlight 1, * * for highlight 2.',
        },
        ...headingHighlightFields(1, CONTENT),
        ...headingHighlightFields(2, CONTENT),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: CONTENT,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom desktop size',
            group: CONTENT,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingSizeMobile',
            type: 'range',
            label: 'Custom mobile size',
            group: CONTENT,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: CONTENT,
            default: BODY_TEXT_DEFAULT,
        },
        {
            id: 'buttonLabel',
            type: 'text',
            label: 'Button label',
            group: CONTENT,
            info: 'Leave blank to hide the button.',
        },
        {
            id: 'buttonLink',
            type: 'link',
            label: 'Button link',
            group: CONTENT,
        },
        {
            id: 'buttonStyleSecondary',
            type: 'checkbox',
            label: 'Use outline button style',
            group: CONTENT,
            default: false,
        },
        // ── Content layout (position / alignment) ────────────────────────────────────
        {
            id: 'desktopContentPosition',
            type: 'select',
            label: 'Desktop content position',
            group: LAYOUT,
            default: 'top',
            options: [
                { value: 'top', label: 'Top' },
                { value: 'right', label: 'Right' },
                { value: 'bottom', label: 'Bottom' },
                { value: 'left', label: 'Left' },
            ],
        },
        {
            id: 'desktopContentAlignment',
            type: 'select',
            label: 'Desktop content alignment',
            group: LAYOUT,
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'hideContentOnMobile',
            type: 'checkbox',
            label: 'Hide content on mobile',
            group: LAYOUT,
            default: false,
        },
        {
            id: 'mobileContentPosition',
            type: 'select',
            label: 'Mobile content position',
            group: LAYOUT,
            default: 'top',
            options: [
                { value: 'top', label: 'Top' },
                { value: 'bottom', label: 'Bottom' },
            ],
        },
        {
            id: 'contentCornerRadius',
            type: 'checkbox',
            label: 'Enable content corner radius',
            group: LAYOUT,
            default: true,
            info: 'Applied when the content and section color schemes differ.',
        },
        // ── Content color scheme ─────────────────────────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Content color scheme',
            group: CONTENT,
            default: 'background-1',
            options: SCHEME_OPTIONS,
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: CONTENT,
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: CONTENT,
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: CONTENT,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Section color scheme (the region behind the whole section) ────────────────
        {
            id: 'sectionColorScheme',
            type: 'select',
            label: 'Section color scheme',
            group: CONTENT,
            default: 'background-1',
            options: SCHEME_OPTIONS,
        },
        {
            id: 'customSectionBackground',
            type: 'color',
            label: 'Custom section background color',
            group: CONTENT,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'sectionColorScheme', equals: 'custom' },
        },
        {
            id: 'customSectionGradientBackground',
            type: 'gradient',
            label: 'Custom section gradient background',
            group: CONTENT,
            info: 'If set, replaces the section background color.',
            visibleWhen: { field: 'sectionColorScheme', equals: 'custom' },
        },
        {
            id: 'customSectionImageBackground',
            type: 'image',
            label: 'Custom section image background',
            group: CONTENT,
            visibleWhen: { field: 'sectionColorScheme', equals: 'custom' },
        },
        {
            id: 'customSectionMobileImageBackground',
            type: 'image',
            label: 'Custom section mobile image background',
            group: CONTENT,
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'sectionColorScheme', equals: 'custom' },
        },
        // ── Image ────────────────────────────────────────────────────────────────────
        {
            id: 'image',
            type: 'image',
            label: 'Image',
            group: IMAGE,
        },
        {
            id: 'desktopImageWidth',
            type: 'select',
            label: 'Desktop image width',
            group: IMAGE,
            default: 'limited',
            options: [
                { value: 'limited', label: 'Limited' },
                { value: 'unlimited', label: 'Maximum possible' },
            ],
        },
        {
            id: 'imageMaxWidth',
            type: 'range',
            label: 'Image max width',
            group: IMAGE,
            min: 300,
            max: 1000,
            step: 50,
            unit: 'px',
            default: 900,
        },
        {
            id: 'fullPageWidth',
            type: 'checkbox',
            label: 'Use full page width',
            group: IMAGE,
            default: false,
        },
        {
            id: 'imageCornerRadius',
            type: 'checkbox',
            label: 'Enable image corner radius',
            group: IMAGE,
            default: false,
            info: 'Applied when the content and section color schemes differ.',
        },
        {
            id: 'mobileImage',
            type: 'image',
            label: 'Mobile image',
            group: IMAGE,
            info: 'If empty, the desktop image is used on mobile too.',
        },
        // ── Hotspot buttons & modals ─────────────────────────────────────────────────
        {
            id: 'hotspotsStyle',
            type: 'select',
            label: 'Hotspot buttons style',
            group: HOTSPOTS,
            default: 'plus',
            options: [
                { value: 'plus', label: 'Plus' },
                { value: 'minimal', label: 'Minimalistic' },
            ],
        },
        {
            id: 'hotspotsColorScheme',
            type: 'select',
            label: 'Hotspot buttons color scheme',
            group: HOTSPOTS,
            default: 'inverse',
            options: SCHEME_OPTIONS_5,
        },
        {
            id: 'displayHotspotsAnimation',
            type: 'checkbox',
            label: 'Display button ripple animation',
            group: HOTSPOTS,
            default: true,
            visibleWhen: { field: 'hotspotsStyle', equals: 'plus' },
        },
        {
            id: 'hotspotOpenEvent',
            type: 'select',
            label: 'Open modal on',
            group: HOTSPOTS,
            default: 'click',
            options: [
                { value: 'click', label: 'Click' },
                { value: 'hover', label: 'Hover' },
            ],
        },
        {
            id: 'hotspotContentColorScheme',
            type: 'select',
            label: 'Hotspot modal color scheme',
            group: HOTSPOTS,
            default: 'background-1',
            options: SCHEME_OPTIONS_5,
        },
        {
            id: 'hotspotImageSize',
            type: 'select',
            label: 'Modal image width',
            group: HOTSPOTS,
            default: 'small',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'full', label: 'Full width' },
            ],
        },
        {
            id: 'containHotspotModal',
            type: 'checkbox',
            label: 'Contain modals inside the main image',
            group: HOTSPOTS,
            default: false,
            info: 'Prevents modals from spilling outside the image on desktop (always on for mobile).',
        },
        {
            id: 'hotspotModalDesktopWidth',
            type: 'range',
            label: 'Hotspot modal desktop width',
            group: HOTSPOTS,
            min: 200,
            max: 600,
            step: 20,
            unit: 'px',
            default: 400,
        },
        {
            id: 'hotspotModalMobileWidth',
            type: 'range',
            label: 'Hotspot modal mobile width',
            group: HOTSPOTS,
            min: 100,
            max: 300,
            step: 10,
            unit: 'px',
            default: 300,
        },
        // ── Product information (inside the modal) ───────────────────────────────────
        {
            id: 'modalHeadingSize',
            type: 'select',
            label: 'Product title size',
            group: PRODUCT,
            default: 'medium',
            options: [
                { value: 'small', label: 'Small' },
                { value: 'medium', label: 'Medium' },
                { value: 'large', label: 'Large' },
            ],
        },
        {
            id: 'mobileModalPosition',
            type: 'select',
            label: 'Mobile modal position',
            group: PRODUCT,
            default: 'inline',
            options: [
                { value: 'inline', label: 'Over/under the button' },
                { value: 'fixed', label: 'Fixed to page' },
            ],
        },
        {
            id: 'displayImage',
            type: 'checkbox',
            label: 'Display product featured image',
            group: PRODUCT,
            default: true,
        },
        {
            id: 'displayPrice',
            type: 'checkbox',
            label: 'Display price',
            group: PRODUCT,
            default: true,
        },
        {
            id: 'displayDiscountBadge',
            type: 'checkbox',
            label: 'Display price discount badge',
            group: PRODUCT,
            default: true,
        },
        {
            id: 'modalButtonLabel',
            type: 'text',
            label: 'Modal button label',
            group: PRODUCT,
            default: 'View product',
            defaultKey: 'collection.go_to_product',
            info: 'Leave empty to hide the button.',
        },
        // Shrine `button_function`: the modal CTA is a link to the product page (default) OR a real add-to-cart
        // (adds the product's first available variant to the cart drawer, like Shrine's `separate-atc-btn`).
        {
            id: 'buttonFunction',
            type: 'select',
            label: 'Button function',
            group: PRODUCT,
            default: 'link',
            options: [
                { value: 'link', label: 'Link to product page' },
                { value: 'atc', label: 'Add to cart' },
            ],
        },
        {
            id: 'displayPriceInBtn',
            type: 'checkbox',
            label: 'Show price in button',
            group: PRODUCT,
            default: false,
            visibleWhen: { field: 'buttonFunction', equals: 'atc' },
        },
        {
            id: 'skipCart',
            type: 'checkbox',
            label: 'Skip cart, go to checkout',
            group: PRODUCT,
            default: false,
            info: 'Add the item and jump straight to checkout instead of opening the cart.',
            visibleWhen: { field: 'buttonFunction', equals: 'atc' },
        },
        // ── Section padding ──────────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: PADDING,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: PADDING,
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    // Inline blocks — one repeatable "Product hotspot", addable only inside this section (Shrine's `hotspot`).
    blocks: [
        {
            type: 'hotspot',
            name: 'Product hotspot',
            fields: [
                {
                    id: 'offsetX',
                    type: 'range',
                    label: 'Horizontal offset',
                    group: 'Hotspot position',
                    min: 0,
                    max: 100,
                    step: 1,
                    unit: '%',
                    default: 30,
                },
                {
                    id: 'offsetY',
                    type: 'range',
                    label: 'Vertical offset',
                    group: 'Hotspot position',
                    min: 0,
                    max: 100,
                    step: 1,
                    unit: '%',
                    default: 30,
                },
                {
                    id: 'enableMobilePosition',
                    type: 'checkbox',
                    label: 'Enable custom mobile positioning',
                    group: 'Mobile hotspot position',
                    default: false,
                    info: 'Enable if you use a separate mobile image.',
                },
                {
                    id: 'mobileOffsetX',
                    type: 'range',
                    label: 'Mobile horizontal offset',
                    group: 'Mobile hotspot position',
                    min: 0,
                    max: 100,
                    step: 1,
                    unit: '%',
                    default: 30,
                },
                {
                    id: 'mobileOffsetY',
                    type: 'range',
                    label: 'Mobile vertical offset',
                    group: 'Mobile hotspot position',
                    min: 0,
                    max: 100,
                    step: 1,
                    unit: '%',
                    default: 30,
                },
                { id: 'product', type: 'product-picker', label: 'Product', group: 'Product' },
                { id: 'text', type: 'richtext', label: 'Description', group: 'Product' },
                {
                    id: 'alignment',
                    type: 'select',
                    label: 'Text alignment',
                    group: 'Product',
                    default: 'left',
                    options: [
                        { value: 'left', label: 'Left' },
                        { value: 'center', label: 'Center' },
                        { value: 'right', label: 'Right' },
                    ],
                },
            ],
        },
    ],
};
export const shoppableImagePresets = [
    {
        name: 'Shoppable image',
        settings: {
            text: BODY_TEXT_DEFAULT,
            headingSize: 'h1',
            desktopContentPosition: 'top',
            mobileContentPosition: 'top',
            colorScheme: 'background-1',
            sectionColorScheme: 'background-1',
            hotspotsColorScheme: 'inverse',
            hotspotContentColorScheme: 'background-1',
            paddingTop: 36,
            paddingBottom: 36,
        },
        blocks: [
            { type: 'hotspot', settings: { offsetX: 30, offsetY: 30 } },
            { type: 'hotspot', settings: { offsetX: 40, offsetY: 70 } },
            { type: 'hotspot', settings: { offsetX: 70, offsetY: 50 } },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const shoppableImageMeta = {
    label: 'Shoppable image',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map